import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Star, 
  ExternalLink, 
  Gift, 
  CheckCircle,
  Facebook,
  Instagram,
  Twitter,
  Linkedin
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewLanding = () => {
  const { businessId } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feedback');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '' });

  useEffect(() => {
    loadBusiness();
  }, [businessId]);

  const loadBusiness = async () => {
    try {
      const response = await axios.get(`${API}/public/review/${businessId}`);
      setBusiness(response.data);
    } catch (error) {
      toast.error('Business not found');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = async (platform, url) => {
    // Open review link in new window
    window.open(url, '_blank');
    
    // Record the review action
    try {
      const response = await axios.post(`${API}/public/review/${businessId}/submit`, {
        customerName: customerInfo.name || null,
        customerEmail: customerInfo.email || null,
        platform: platform,
        reviewGiven: true
      });

      if (response.data.hasCoupon && response.data.coupon) {
        setCoupon(response.data.coupon);
        setShowCoupon(true);
        toast.success('Thank you! Here\'s your reward!');
      } else {
        toast.success('Thank you for your review!');
      }
    } catch (error) {
      console.error('Failed to record review');
    }
  };

  const getSocialIcon = (platform) => {
    const p = platform.toLowerCase();
    if (p.includes('facebook')) return <Facebook className="w-5 h-5" />;
    if (p.includes('instagram')) return <Instagram className="w-5 h-5" />;
    if (p.includes('twitter') || p.includes('x.com')) return <Twitter className="w-5 h-5" />;
    if (p.includes('linkedin')) return <Linkedin className="w-5 h-5" />;
    return <ExternalLink className="w-5 h-5" />;
  };

  const getPlatformColor = (platform) => {
    const p = platform.toLowerCase();
    if (p === 'google') return 'from-red-500 to-orange-500';
    if (p === 'yelp') return 'from-red-600 to-red-700';
    if (p === 'facebook') return 'from-blue-600 to-blue-700';
    if (p === 'tripadvisor') return 'from-green-600 to-green-700';
    return 'from-gray-600 to-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600">Business not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showCoupon && coupon) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Thank You!
              </h2>
              <p className="text-gray-600">
                Your review helps us and our community. Here's your reward:
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-dashed border-orange-300 rounded-lg p-6">
              <Gift className="w-12 h-12 text-orange-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {coupon.businessName}
              </h3>
              <div className="bg-white rounded-lg p-4 mb-3">
                <p className="text-sm text-gray-600 mb-2">Coupon Code:</p>
                <p className="text-3xl font-bold text-orange-600 tracking-wider">
                  {coupon.code}
                </p>
              </div>
              <p className="text-gray-700 font-medium mb-2">{coupon.description}</p>
              {coupon.expiryDate && (
                <p className="text-sm text-gray-500">Expires: {coupon.expiryDate}</p>
              )}
              {coupon.termsAndConditions && (
                <p className="text-xs text-gray-400 mt-3">{coupon.termsAndConditions}</p>
              )}
            </div>

            <Button
              onClick={() => {
                // Copy coupon code
                navigator.clipboard.writeText(coupon.code);
                toast.success('Coupon code copied!');
              }}
              className="w-full"
            >
              Copy Coupon Code
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-4 md:p-8"
      style={{
        background: `linear-gradient(to bottom right, ${business.primaryColor}15, ${business.primaryColor}05)`
      }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Business Header */}
        <Card className="mb-6">
          <CardContent className="pt-6 text-center">
            {business.logo && (
              <img 
                src={business.logo} 
                alt={business.businessName}
                className="w-24 h-24 mx-auto mb-4 rounded-full object-cover"
              />
            )}
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {business.businessName}
            </h1>
            {business.description && (
              <p className="text-gray-600 mb-4">{business.description}</p>
            )}
            <div className="flex items-center justify-center gap-2 text-yellow-500">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className="w-6 h-6 fill-current" />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Customer Info (Optional) */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4 text-center">
              Help us serve you better (Optional)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                placeholder="Your Name"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
              />
              <Input
                type="email"
                placeholder="Your Email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Review Links */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold text-center mb-4">
              Share Your Experience
            </h2>
            <p className="text-center text-gray-600 mb-6">
              Choose a platform and leave us a review to receive your reward!
            </p>
            
            <div className="space-y-3">
              {business.reviewLinks
                .sort((a, b) => (b.priority || 0) - (a.priority || 0))
                .map((link, idx) => (
                  <Button
                    key={idx}
                    onClick={() => handleReviewClick(link.platform, link.url)}
                    className={`w-full bg-gradient-to-r ${getPlatformColor(link.platform)} text-white hover:opacity-90 flex items-center justify-center gap-3 h-14 text-lg`}
                  >
                    <Star className="w-5 h-5 fill-current" />
                    <span className="capitalize">Leave {link.platform} Review</span>
                    <ExternalLink className="w-5 h-5" />
                  </Button>
                ))}
            </div>

            {business.couponOffers.length > 0 && (
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-yellow-800 mb-2">
                  <Gift className="w-5 h-5" />
                  <span className="font-semibold">Special Offer!</span>
                </div>
                <p className="text-sm text-yellow-700">
                  Leave a review and get a special coupon code for your next visit!
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Social Links */}
        {business.socialLinks.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-center mb-4">
                Follow Us
              </h2>
              <div className="flex justify-center gap-3 flex-wrap">
                {business.socialLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    {getSocialIcon(link.platform)}
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReviewLanding;
